import { CommonModule } from '@angular/common';
import { Component, Input, OnInit, Output, EventEmitter, OnDestroy, ViewChild, HostListener } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TabsModule } from 'primeng/tabs';
import { AvatarModule } from 'primeng/avatar';
import { DialogComponent } from '../../../../../../@core/components/ui/dialog/dialog.component';
import { HlmDialogHeaderComponent } from '../../../../../../@core/components/spartan/dialog/hlm-dialog-header.component';
import { InputTextModule } from 'primeng/inputtext';
import { MenuItem } from 'primeng/api';
import { SearchBarComponent } from '../../../../../../@shared/components/search-bar/search-bar.component';
import { OnlyOfficeDocEditorComponent } from '../../../../../../@shared/components/only-office-doc-editor/only-office-doc-editor.component';
import { TEMPLATE_GENERATION_STATUS, TemplateLibraryService } from '../../../../../services/template-library.service';
import { ToastService } from '../../../../../../@core/services/toast.service';
import { ITemplateLibrary } from '../template-library.component';
import { IConfig } from '@onlyoffice/document-editor-angular';
import { IAttachment, IUser, IDocumentVersion } from 'src/app/@core/models/model';
import { FILE_TYPE, ONLY_OFFICE_EDITOR_TYPE } from 'src/app/@core/models/enums';
import { AuthenticationService } from 'src/app/@core/services/authentication.service';
import { environment } from 'src/environments/environment.dev';
import { JWTService } from 'src/app/contract-management/services/jwt.service';
import { PopoverComponent } from '../../../../../../@core/components/ui/popover/popover.component';
import { FullNamePipe } from '../../../../../../@shared/pipes/fullName.pipe';
import { UserAvatarPipe } from '../../../../../../@shared/pipes/user-avatar-pipe';
import { ButtonComponent } from '../../../../../../@core/components/ui/button/button.component';
import { AddProjectTagsComponent, IProjectTagType } from './add-project-tags-dialog/add-project-tags-dialog.component';
import { retrieveDisplayNameForUser, downloadDocument, base64ToBlob } from '../../../../../../common/utils';
import { IconComponent } from 'src/app/@core/components/ui/icon/icon.component';
import { UserNameDisplayComponent } from "src/app/@shared/components/user-name-display";
import { TEMPLATE_LIBRARY_SVG } from 'src/app/common/svgs';
import { SanitizeHtmlPipe } from 'src/app/@shared/pipes/sanitizeHtml.pipe';

@Component({
    selector: 'app-view-template',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        TabsModule,
        AvatarModule,
        DialogComponent,
        HlmDialogHeaderComponent,
        InputTextModule,
        SearchBarComponent,
        OnlyOfficeDocEditorComponent,
        PopoverComponent,
        UserAvatarPipe,
        ButtonComponent,
        AddProjectTagsComponent,
        UserNameDisplayComponent,
        SanitizeHtmlPipe,
        FullNamePipe
    ],
    templateUrl: './view-template.component.html',
    styleUrl: './view-template.component.scss'
})
export class ViewTemplateComponent implements OnInit, OnDestroy {
    @Input() templateLibraryId: string = '';
    @Output() close = new EventEmitter<void>();

    template: ITemplateLibrary = {} as ITemplateLibrary;
    attachment: IAttachment = {} as IAttachment;
    TEMPLATE_LIBRARY_SVG = TEMPLATE_LIBRARY_SVG;
    loading: boolean = false;
    saving: boolean = false;
    onlyOfficeConfig: IConfig = {};
    activeVersionsTab: string = 'versions';
    versionsSearchValue: string = '';
    activityLogsSearchValue: string = '';
    private jwtService = new JWTService();

    // Version management
    versions: IDocumentVersion[] = [];
    filteredVersions: IDocumentVersion[] = [];
    selectedVersion!: IDocumentVersion;
    currentVersion!: IDocumentVersion;
    versionLoading: boolean = false;

    // Activity logs management
    activityLogs: any[] = [];
    filteredActivityLogs: any[] = [];
    activityLogsLoading: boolean = false;

    // Restore version dialog
    showRestoreVersionDialog: boolean = false;
    versionToRestore: IDocumentVersion | null = null;
    restoringVersion: boolean = false;

    // Edit version name dialog
    showEditVersionNameDialog: boolean = false;
    versionToEdit: IDocumentVersion | null = null;
    editedVersionName: string = '';
    updatingVersionName: boolean = false;

    // Project tags UI
    showProjectTags: boolean = false;

    // Editor ready state - true only after OnlyOffice fires onDocumentReady
    isEditorReady: boolean = false;

    currentUser: any;
    config!: IConfig;
    docFormat: string = 'docx';
    @ViewChild('docEditor') docEditorComp!: OnlyOfficeDocEditorComponent;

    constructor(
        private templateLibraryService: TemplateLibraryService,
        private toast: ToastService,
        private authService: AuthenticationService
    ) {
        this.authService.currentUser.subscribe(res => {
            this.currentUser = res as IUser
        });
    }

    ngOnInit() {
        if (this.templateLibraryId) {
            this.loadTemplate();
        }
    }

    onTabChange(event: any) {
        if (this.activeVersionsTab === 'versions') {
            // Refresh versions when switching to versions tab
            this.getVersions();
        } else if (this.activeVersionsTab === 'activity' && this.activityLogs.length === 0) {
            this.getActivityLogs();
        }
    }

    ngOnDestroy() {
        this.config = {};
    }

    insertTextAtCursor(text: string) {
        if (!this.docEditorComp) {
            console.warn('Editor component not ready yet');
            return;
        }
        // this.docEditorComp.insertTextAtCursor(text);
    }

    onInsertTag() {
        // Insert square braces as placeholder for AI content insertion
        this.insertTextAtCursor('[]');
    }

    onAddProjectTagsClick() {
        this.showProjectTags = true;
    }

    onProjectTagsClose() {
        this.showProjectTags = false;
    }

    onProjectTagsInsert(selectedTags: IProjectTagType[]) {
        // Insert selected tags into the document
        // For now, insert as text - can be enhanced later
        const tagText = selectedTags.map(tag => `[${tag.name}]`).join(' ');
        this.insertTextAtCursor(tagText);
        this.toast.success(`${selectedTags.length} tag(s) inserted successfully`);
        this.showProjectTags = false;
    }

    loadTemplate() {
        this.loading = true;
        this.templateLibraryService.getTemplate(this.templateLibraryId).subscribe({
            next: (res: any) => {
                if (res.success && res.data) {
                    if (res.data.generationStatus == TEMPLATE_GENERATION_STATUS.IN_PROGRESS) {
                        this.toast.info('Template is being generated, please wait...');
                        this.onClose();
                        return;
                    }
                    this.template = res.data;
                    this.attachment = this.template.attachment as IAttachment
                    this.setupConfig();
                    this.getVersions();
                } else {
                    this.toast.error('Failed to load template', 'error');
                }
                this.loading = false;
            },
            error: () => {
                this.toast.error('Failed to load template', 'error');
                this.loading = false;
            }
        });
    }

    getVersions(showLatest: boolean = false) {
        this.versionLoading = true;
        const filters = {
            search: this.versionsSearchValue || '',
            skip: 0,
            limit: 20
        };
        this.templateLibraryService.getTemplateVersions(this.templateLibraryId, filters).subscribe({
            next: (res: any) => {
                if (res.success) {
                    this.versions = res.data.versions || [];
                    this.filteredVersions = this.versions.slice(1);
                    if (this.versions.length > 0) {
                        this.currentVersion = this.versions[0];
                        if (!this.selectedVersion || showLatest) {
                            this.showVersion(this.currentVersion);
                        }
                    }
                }
                this.versionLoading = false;
            },
            error: (err: any) => {
                this.toast.error('Failed to load versions', 'error');
                this.versionLoading = false;
            }
        });
    }

    showVersion(item: IDocumentVersion) {
        if (!item) return;
        this.selectedVersion = item;
        this.templateLibraryService.getTemplateVersionURL(this.templateLibraryId, item.version).subscribe({
            next: (res: any) => {
                if (res.success) {
                    const versionAttachment = res.data.attachment as IAttachment;
                    const officeDocType = this.getOfficeDocTypeFromMimeType(versionAttachment.type);
                    const url = res.data.url;
                    const key = res.data.key;
                    this.getDocumentEditorConfig(key, url, officeDocType, versionAttachment);
                }
            },
            error: () => {
                this.toast.error('Failed to load version', 'error');
            }
        });
    }

    private getOfficeDocTypeFromMimeType(mimeType: string): string {
        const formatMap: Record<string, string> = {
            'application/pdf': 'pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx'
        };
        this.docFormat = formatMap[mimeType] || 'docx';
        return this.docFormat === 'pdf' ? 'pdf' : 'word';
    }

    getDocumentEditorConfig(key: string, url: string, officeDocType: string, attachment?: IAttachment) {
        const isCurrentVersion = this.isCurrentVersionSelected();
        this.isEditorReady = false;
        this.config = this.createOnlyOfficeConfig(key, url, officeDocType, officeDocType, false, isCurrentVersion);
    }

    onEditorReady() {
        this.isEditorReady = true;
    }

    onClose() {
        this.close.emit();
    }

    onDownloadTemplate() {
        if (!this.templateLibraryId) {
            this.toast.error('Template ID not found', 'error');
            return;
        }

        this.templateLibraryService.bulkDownloadTemplates([this.templateLibraryId]).subscribe({
            next: (res: any) => {
                if (res.success && res.data && res.data.length > 0) {
                    const templateData = res.data[0];
                    const blob = base64ToBlob(templateData.buffer, templateData.mimeType);
                    const link = document.createElement('a');
                    link.href = URL.createObjectURL(blob);
                    link.download = templateData.filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(link.href);
                    this.toast.success('Template downloaded successfully', 'success');
                } else {
                    this.toast.error('Failed to download template', 'error');
                }
            },
            error: (error: any) => {
                this.toast.error(error?.message || 'Failed to download template', 'error');
            }
        });
    }

    isCurrentVersionSelected(): boolean {
        return this.selectedVersion && this.currentVersion && this.selectedVersion._id === this.currentVersion._id;
    }

    onSave() {
        if (!this.isEditorReady) {
            return;
        }

        if (!this.config?.document?.key) {
            this.toast.error('Document key not found', 'error');
            return;
        }

        if (this.saving) {
            return;
        }

        this.saving = true;
        const key = this.config.document.key;
        const userId = this.currentUser?._id;

        this.templateLibraryService.forceSaveTemplate(this.templateLibraryId, key, userId).subscribe({
            next: (res: any) => {
                this.saving = false;
                if (res.success) {
                    this.toast.success('Document saved successfully', 'success');
                    this.getVersions(true);
                } else {
                    this.toast.error(res.message || 'Failed to save document', 'error');
                }
            },
            error: (err: any) => {
                this.saving = false;
                this.toast.error(err?.error?.message || 'Failed to save document', 'error');
            }
        });
    }

    @HostListener('document:keydown', ['$event'])
    handleKeyboardEvent(event: KeyboardEvent) {
        // Handle Cmd+S (Mac) or Ctrl+S (Windows/Linux)
        if ((event.metaKey || event.ctrlKey) && event.key === 's') {
            event.preventDefault();
            this.onSave();
        }
    }

    // Version menu items
    getMenuItems(version: IDocumentVersion): MenuItem[] {
        return [
            {
                label: 'Copy Link',
                icon: 'pi pi-link',
                command: () => {
                    this.templateLibraryService.getTemplateVersionURL(this.templateLibraryId, version.version).subscribe({
                        next: (res: any) => {
                            if (res.success) {
                                this.copyToClipboard(res.data.url);
                                this.toast.success('Link Copied Successfully');
                            } else {
                                this.toast.error('Failed to Copy Link');
                            }
                        },
                        error: (err: any) => {
                            this.toast.error('Failed to Copy Link');
                        }
                    });
                }
            },
            {
                label: 'Restore Version',
                icon: 'pi pi-refresh',
                command: () => {
                    this.openRestoreVersionDialog(version);
                }
            },
            {
                label: 'Download',
                icon: 'pi pi-download',
                command: () => {
                    const index = this.filteredVersions.indexOf(version);
                    this.downloadVersion(version.version, index);
                }
            },
            {
                label: 'Delete',
                icon: 'pi pi-trash',
                command: () => {
                    this.deleteVersion(version.version);
                }
            }
        ];
    }

    handleMenuClick(item: MenuItem, popover: PopoverComponent) {
        if (item.command) {
            item.command({} as any);
        }
        popover.hide();
    }

    copyToClipboard(url: string): void {
        if (navigator.clipboard) {
            navigator.clipboard.writeText(url);
        }
    }

    downloadVersion(versionId: string, index: number) {
        this.versionLoading = true;
        this.templateLibraryService.downloadTemplateVersion(this.templateLibraryId, versionId).subscribe({
            next: (res: any) => {
                if (res.success) {
                    const versionNumber = this.getVersionNumber(index);
                    downloadDocument(
                        res.data.data,
                        `${this.template.name} - Version${versionNumber}`,
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
                    );
                    this.versionLoading = false;
                }
            },
            error: () => {
                this.toast.error('Failed to download version');
                this.versionLoading = false;
            }
        });
    }

    private getVersionNumber(index: number): number {
        return this.versions.length - index;
    }

    deleteVersion(versionId: string) {
        this.templateLibraryService.deleteTemplateVersion(this.templateLibraryId, versionId).subscribe({
            next: (res: any) => {
                if (res.success) {
                    this.toast.success('Version Deleted Successfully');
                    this.getVersions();
                }
            },
            error: (err: any) => {
                this.toast.error('Failed to delete version');
            }
        });
    }


    onVersionsSearchChange() {
        this.getVersions();
    }

    getActivityLogs() {
        this.activityLogsLoading = true;
        this.templateLibraryService.getTemplateAuditLogs(this.templateLibraryId).subscribe({
            next: (res: any) => {
                if (res.success) {
                    this.activityLogs = res.data || [];
                    this.filterActivityLogs();
                }
                this.activityLogsLoading = false;
            },
            error: () => {
                this.toast.error('Failed to load activity logs', 'error');
                this.activityLogsLoading = false;
            }
        });
    }

    filterActivityLogs() {
        if (!this.activityLogsSearchValue || this.activityLogsSearchValue.trim() === '') {
            this.filteredActivityLogs = this.activityLogs;
            return;
        }
        const searchTerm = this.activityLogsSearchValue.toLowerCase();
        this.filteredActivityLogs = this.activityLogs.filter((log: any) => {
            const description = (log.description || '').toLowerCase();
            const action = (log.action || '').toLowerCase();
            const userName = log.createdBy ? retrieveDisplayNameForUser(log.createdBy).toLowerCase() : '';
            return description.includes(searchTerm) || action.includes(searchTerm) || userName.includes(searchTerm);
        });
    }

    onActivityLogsSearchChange() {
        this.filterActivityLogs();
    }

    openRestoreVersionDialog(version: IDocumentVersion) {
        this.versionToRestore = version;
        this.showRestoreVersionDialog = true;
    }

    onCloseRestoreVersionDialog() {
        this.showRestoreVersionDialog = false;
        this.versionToRestore = null;
    }

    onConfirmRestoreVersion() {
        if (!this.versionToRestore) {
            return;
        }

        this.restoringVersion = true;
        this.templateLibraryService.restoreTemplateVersion(this.templateLibraryId, this.versionToRestore.version).subscribe({
            next: (res: any) => {
                this.restoringVersion = false;
                if (res.success) {
                    this.toast.success('Version Restored Successfully');
                    this.onCloseRestoreVersionDialog();
                    this.getVersions();
                    this.loadTemplate();
                    this.selectedVersion = this.currentVersion;
                } else {
                    this.toast.error('Failed to restore version');
                }
            },
            error: (err: any) => {
                this.restoringVersion = false;
                this.toast.error('Failed to restore version');
            }
        });
    }

    openEditVersionNameDialog(version: IDocumentVersion) {
        this.versionToEdit = version;
        this.editedVersionName = version.name || '';
        this.showEditVersionNameDialog = true;
    }

    onCloseEditVersionNameDialog() {
        this.showEditVersionNameDialog = false;
        this.versionToEdit = null;
        this.editedVersionName = '';
    }

    onConfirmEditVersionName() {
        if (!this.versionToEdit || !this.editedVersionName || this.editedVersionName.trim() === '') {
            this.toast.error('Version name cannot be empty', 'error');
            return;
        }

        if (this.editedVersionName.trim() === this.versionToEdit.name) {
            this.onCloseEditVersionNameDialog();
            return;
        }

        this.updatingVersionName = true;
        this.templateLibraryService.updateTemplateVersionName(
            this.templateLibraryId,
            this.versionToEdit.version,
            this.editedVersionName.trim()
        ).subscribe({
            next: (res: any) => {
                this.updatingVersionName = false;
                if (res.success) {
                    this.toast.success('Version name updated successfully');
                    // Update the version name in the local array 
                    const newName = this.editedVersionName.trim();
                    const versionIndex = this.versions.findIndex(v => v._id === this.versionToEdit!._id);
                    if (versionIndex !== -1) {
                        this.versions[versionIndex].name = newName;
                        if (this.versions[versionIndex] === this.currentVersion) {
                            this.currentVersion.name = newName;
                        }
                        if (this.selectedVersion && this.selectedVersion._id === this.versionToEdit!._id) {
                            this.selectedVersion.name = newName;
                        }
                    }
                    this.onCloseEditVersionNameDialog();
                } else {
                    this.toast.error(res.message || 'Failed to update version name', 'error');
                }
            },
            error: (err: any) => {
                this.updatingVersionName = false;
                this.toast.error(err?.error?.message || 'Failed to update version name', 'error');
            }
        });
    }

    getDocumentFormat(attachment: IAttachment): FILE_TYPE {
        switch (attachment?.type) {
            case 'application/pdf':
                return FILE_TYPE.PDF
            case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
                return FILE_TYPE.WORD
            case 'application/vnd.ms-excel':
            case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
                return FILE_TYPE.EXCEL
            default:
                return FILE_TYPE.WORD
        }
    }

    getEditorTypeForFile(fileType: FILE_TYPE): ONLY_OFFICE_EDITOR_TYPE {
        switch (fileType) {
            case FILE_TYPE.WORD:
                return ONLY_OFFICE_EDITOR_TYPE.WORD
            case FILE_TYPE.PDF:
                return ONLY_OFFICE_EDITOR_TYPE.PDF
            case FILE_TYPE.EXCEL:
                return ONLY_OFFICE_EDITOR_TYPE.EXCEL
            default:
                return ONLY_OFFICE_EDITOR_TYPE.WORD
        }
    }

    setupConfig() {
        if (!this.template.attachment) {
            return;
        }
        const docFormat = this.getDocumentFormat(this.attachment as IAttachment);
        const editorType = this.getEditorTypeForFile(docFormat);
        // Extract attachment ID - handle both string and object types
        const attachmentId = typeof this.template.attachment === 'string'
            ? this.template.attachment
            : (this.template.attachment as IAttachment)._id;
        const key = this.templateLibraryId + '-' + attachmentId;
        // Always allow editing for current version (when setupConfig is called, it's the current version)
        this.config = this.createOnlyOfficeConfig(key, this.template.url ?? '', editorType, editorType, true, true);
    }

    private createOnlyOfficeConfig(
        key: string,
        url: string,
        documentType: string,
        editorType: string,
        includePlugins: boolean = false,
        currentVersionSelected: boolean = true
    ): IConfig {
        const tokenConfig: Partial<IConfig> = {
            document: {
                fileType: this.docFormat as any,
                key: currentVersionSelected ? key + '-' + Math.random().toString(36).substring(2, 15) : key,
                title: this.template.name ?? 'Document Preview',
                url: url,
                permissions: {
                    chat: false,
                    comment: false,
                    download: true,
                    edit: currentVersionSelected,
                    fillForms: currentVersionSelected,
                    modifyContentControl: currentVersionSelected,
                    modifyFilter: currentVersionSelected,
                    print: true,
                    protect: currentVersionSelected,
                    review: false,
                    userInfoGroups: [],
                },
            },
            documentType: documentType,
            editorConfig: {
                user: {
                    group: this.templateLibraryId,
                    id: this.currentUser?.email,
                    image: this.currentUser?.profilePicture?.url
                        ? `${environment.s3Url}/${this.currentUser.profilePicture.url}?timestamp=${new Date().getTime()}`
                        : '',
                    name: retrieveDisplayNameForUser(this.currentUser as IUser)
                },
                embedded: {
                    toolbarDocked: "top",
                },
                customization: {
                    toolbarHideFileName: true,
                    hideRightMenu: true,
                    compactToolbar: true,
                    compactHeader: true,
                    comments: false,
                    forcesave: true,
                    autosave: includePlugins ? false : undefined,
                    uiTheme: "theme-gray",
                    logo: {
                        image: `${environment.s3Url}/inndoc-ai/assets/images/3.png`,
                    },
                    zoom: -2, // Fit to width
                },
                callbackUrl: environment.apiUrl + '/public/save-template' + "?templateId=" + this.templateLibraryId + "&userId=" + this.currentUser?._id,
                ...(includePlugins && {
                    plugins: {
                        autostart: [],
                        pluginsData: []
                    }
                })
            },
            height: "100%",
            width: "100%",
        };
        const token = this.jwtService.signToken(tokenConfig, environment.jwtSecret);
        return {
            ...tokenConfig,
            token: token
        } as IConfig;
    }
}

